// src/modules/IntegratedMessaging/components/interaction/CreateButton.tsx
import { Button } from '@shared/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@shared/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { faChevronDown, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@shared/hooks/usePermissions';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriers } from '../../hooks/useCouriers';
import type { Courier } from '../../models/IntegratedMessaging';

interface CreateButtonProps {
  onCreate: (courier: Courier) => void;
}

/**
 * Componente para el botón de crear mensajería integrada
 * Permite seleccionar un courier para configurar
 */
export const CreateButton: FC<CreateButtonProps> = ({ onCreate }) => {
  const { t } = useTranslation('integratedmessagingModule');
  const { canAccess } = usePermissions();
  const { data: couriers = [], isLoading } = useCouriers();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  // Verificar si el usuario puede configurar mensajerías
  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['shipping.couriers.create']);

  const filtered = couriers.filter((c) => c.name.toLowerCase().includes(filter.toLowerCase()));

  const handleSelect = (id: number) => {
    const courier = couriers.find((c) => c.id === id);
    if (courier) {
      onCreate(courier);
    }
    setOpen(false);
  };

  // Si no tiene permisos, no mostrar el botón
  if (!canCreate) {
    return null;
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button size="lg" variant="primary" data-testid="create-messaging-button">
          <span className="block sm:hidden">
            <FontAwesomeIcon icon={faPlus} />
          </span>
          <span className="hidden sm:block">
            {t('interaction.create')}
            <FontAwesomeIcon icon={faChevronDown} className="ml-1" />
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="center" side="bottom" className="w-64 p-0">
        <Command>
          <CommandInput
            placeholder={t('interaction.searchPlaceholder')}
            value={filter}
            onValueChange={setFilter}
            className="border-b"
          />
          <CommandList>
            {isLoading && <CommandEmpty>{t('interaction.loading')}</CommandEmpty>}

            {!isLoading && filtered.length === 0 && <CommandEmpty>{t('interaction.notFound')}</CommandEmpty>}

            {!isLoading &&
              filtered.map((c) => (
                <CommandItem key={c.id} value={c.name} onSelect={() => handleSelect(c.id)}>
                  {c.name}
                </CommandItem>
              ))}
          </CommandList>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
